import {defaultWebinars,makeDemoContacts,defaultWorkflows} from './data';

const DATA_KEYS={
  contacts:'awsa_contacts',
  webinars:'awsa_webinars',
  sessions:'awsa_sessions',
  registrations:'awsa_registrations',
  deliveries:'awsa_deliveries',
  workflows:'awsa_workflows',
  logs:'awsa_logs'
};

const get=(key,fallback)=>{
  try {
    const value=localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const loadContacts=()=>get(DATA_KEYS.contacts,makeDemoContacts());
export const loadWebinars=()=>get(DATA_KEYS.webinars,defaultWebinars);
export const loadWorkflows=()=>get(DATA_KEYS.workflows,defaultWorkflows);
export const loadLogs=()=>get(DATA_KEYS.logs,[]);
export const loadDeliveries=()=>get(DATA_KEYS.deliveries,[]);

export const save=(key,value)=>{
  try {
    const serialized=JSON.stringify(value);
    if (localStorage.getItem(key)===serialized) return;
    localStorage.setItem(key,serialized);
    window.dispatchEvent(new CustomEvent('awsa-data',{detail:{key}}));
  } catch {
    window.alert('ブラウザーの保存容量を確認してください。データを書き込めませんでした。');
  }
};

const makeSession=(webinar,index=0,date=webinar.date)=>({
  id:`${webinar.id}-session-${index+1}`,
  webinarId:webinar.id,
  date,
  status:webinar.status || '下書き',
  seriesOrder:index+1,
  createdAt:new Date().toISOString()
});

export function loadSessions() {
  const stored=localStorage.getItem(DATA_KEYS.sessions);
  if (stored) return get(DATA_KEYS.sessions,[]);
  return loadWebinars().flatMap(webinar=>buildSessions(webinar,webinar.occurrenceCount || 1));
}

function addMonths(date,months) {
  const next=new Date(date);
  const day=next.getDate();
  next.setDate(1);
  next.setMonth(next.getMonth()+months);
  next.setDate(Math.min(day,new Date(next.getFullYear(),next.getMonth()+1,0).getDate()));
  return next;
}

function toLocalDateTime(date) {
  const pad=value=>String(value).padStart(2,'0');
  return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function buildSessions(webinar,count=1) {
  const start=new Date(webinar.date);
  if (Number.isNaN(start.getTime())) return [];
  const total=webinar.recurrence==='single' ? 1 : Math.max(1,Math.min(12,Number(count)||1));
  return Array.from({length:total},(_,index)=>{
    if (webinar.recurrence==='monthly') {
      return makeSession(webinar,index,toLocalDateTime(addMonths(start,index)));
    }
    const date=new Date(start);
    if (webinar.recurrence==='weekly') date.setDate(date.getDate()+index*7);
    if (webinar.recurrence==='biweekly') date.setDate(date.getDate()+index*14);
    return makeSession(webinar,index,toLocalDateTime(date));
  });
}

export function loadRegistrations() {
  const stored=localStorage.getItem(DATA_KEYS.registrations);
  if (stored) return get(DATA_KEYS.registrations,[]);
  const contacts=loadContacts();
  const session=loadSessions()[0];
  if (!session) return [];
  return contacts.map((contact,index)=>({
    id:`legacy-registration-${contact.id || index}`,
    contactId:contact.id,
    sessionId:session.id,
    attendance:contact.attendance || '未確認',
    materials:Boolean(contact.materials),
    survey:contact.survey || null,
    createdAt:contact.lastTouch || new Date().toISOString(),
    isDemo:contact.isDemo!==false,
    emailStatus:contact.emailStatus || '配信可能',
    meeting:contact.meeting || '未予約',
    partner:contact.partner || '未登録',
    opportunity:contact.opportunity || '未作成'
  }));
}

export function scheduleForRegistration(registration,session,existing=[]) {
  if (!session) return existing;
  const date=new Date(session.date);
  const reminders=[
    {key:'confirmation',title:'申込受付完了',date:new Date(registration.createdAt || Date.now())},
    {key:'three-days',title:'開催3日前のご案内',date:new Date(date.getTime()-3*86400000)},
    {key:'one-day',title:'前日リマインド',date:new Date(date.getTime()-86400000)},
    {key:'one-hour',title:'1時間前リマインド',date:new Date(date.getTime()-3600000)},
    {key:'after',title:'開催後のお礼・資料案内',date:new Date(date.getTime()+2*3600000)}
  ];
  const retained=existing.filter(item=>item.registrationId!==registration.id || item.status!=='予定');
  const pending=reminders.map(item=>({
    id:`delivery-${registration.id}-${item.key}`,
    registrationId:registration.id,
    sessionId:session.id,
    key:item.key,
    title:item.title,
    scheduledAt:item.date.toISOString(),
    status:'予定',
    deliveryMode:'未接続・送信なし'
  }));
  return [...pending,...retained];
}

export function rescheduleSessionDeliveries(sessionId,sessions,registrations,deliveries) {
  const session=sessions.find(item=>item.id===sessionId);
  if (!session) return deliveries;
  return registrations
    .filter(item=>item.sessionId===sessionId)
    .reduce((list,registration)=>scheduleForRegistration(registration,session,list),deliveries);
}

export function scoreContact(contact) {
  const reasons=[];
  let score=15;
  const customers=Number(contact.customers)||0;
  const leads=Number(contact.opportunities)||0;
  score+=Math.min(25,Math.round(customers/4));
  reasons.push(`既存法人顧客 ${customers}社を評価`);
  score+=Math.min(20,leads*2);
  reasons.push(`月間商談機会 ${leads}件を評価`);
  const intent=String(contact.intent || '');
  score+=intent.includes('具体') ? 25 : intent.includes('検討') ? 16 : 8;
  reasons.push(`提案意欲「${intent || '未回答'}」を評価`);
  score+=contact.attendance==='参加' ? 10 : contact.attendance==='途中退出' ? 6 : contact.attendance==='欠席' ? 2 : 0;
  reasons.push(`参加状況「${contact.attendance || '未確認'}」を反映`);
  score+=contact.survey ? 5 : 0;
  score+=contact.materials ? 5 : 0;
  if (contact.survey) reasons.push('アンケート回答あり');
  if (contact.materials) reasons.push('資料取得あり');
  score=Math.min(100,score);
  return {score,rank:score>=70 ? 'A' : score>=45 ? 'B' : 'C',reasons};
}

export function appendLog(logs,type,detail) {
  return [{id:`${Date.now()}-${Math.random()}`,type,detail,at:new Date().toISOString(),status:'完了'},...logs];
}

export function exportLocalData() {
  return {
    app:'webinarflow-local-demo',
    schemaVersion:1,
    exportedAt:new Date().toISOString(),
    data:Object.fromEntries(Object.entries(DATA_KEYS).map(([name,key])=>[name,get(key,
      name==='contacts' ? makeDemoContacts() :
      name==='webinars' ? defaultWebinars :
      name==='workflows' ? defaultWorkflows : []
    )]))
  };
}

function makeUniqueId(id,collection) {
  let candidate=id;
  while (collection.some(item=>item.id===candidate)) {
    candidate=`${id}-import-${Math.random().toString(36).slice(2,8)}`;
  }
  return candidate;
}

function mergeCollection(existing,incoming,{identity,transform=record=>record}={}) {
  const rows=[...existing];
  const idMap=new Map();
  const identityMap=new Map();
  if (identity) rows.forEach(record=>{
    const value=identity(record);
    if (value) identityMap.set(value,record.id);
  });

  incoming.forEach(original=>{
    const record=transform({...original});
    if (!record.id) throw new Error('IDのないレコードが含まれているため、読み込めません。');
    const identityValue=identity?.(record);
    const matchId=identityValue && identityMap.get(identityValue);
    if (matchId) {
      idMap.set(original.id,matchId);
      return;
    }
    const sameId=rows.find(item=>item.id===record.id);
    if (sameId) {
      if (JSON.stringify(sameId)===JSON.stringify(record)) {
        idMap.set(original.id,sameId.id);
        return;
      }
      record.id=makeUniqueId(record.id,rows);
    }
    rows.push(record);
    idMap.set(original.id,record.id);
    if (identityValue) identityMap.set(identityValue,record.id);
  });
  return {rows,idMap};
}

export function importLocalData(text) {
  let parsed;
  try {
    parsed=JSON.parse(text);
  } catch {
    throw new Error('JSONファイルを読み取れません。');
  }
  const incoming=parsed?.data || parsed;
  const names=Object.keys(DATA_KEYS);
  if (!incoming || typeof incoming!=='object' || Array.isArray(incoming)) {
    throw new Error('バックアップの形式が正しくありません。');
  }
  names.forEach(name=>{
    if (incoming[name]!==undefined && !Array.isArray(incoming[name])) {
      throw new Error(`${name}のデータ形式が正しくありません。`);
    }
  });

  const current={
    contacts:loadContacts(),webinars:loadWebinars(),sessions:loadSessions(),
    registrations:loadRegistrations(),deliveries:loadDeliveries(),
    workflows:loadWorkflows(),logs:loadLogs()
  };
  const source=name=>incoming[name] || [];
  const webinarResult=mergeCollection(current.webinars,source('webinars'));
  const contactResult=mergeCollection(current.contacts,source('contacts'),{
    identity:record=>String(record.email || '').trim().toLowerCase()
  });
  const sessionResult=mergeCollection(current.sessions,source('sessions'),{
    transform:record=>({...record,webinarId:webinarResult.idMap.get(record.webinarId) || record.webinarId})
  });
  const registrationResult=mergeCollection(current.registrations,source('registrations'),{
    transform:record=>({
      ...record,
      contactId:contactResult.idMap.get(record.contactId) || record.contactId,
      sessionId:sessionResult.idMap.get(record.sessionId) || record.sessionId
    })
  });
  const deliveryResult=mergeCollection(current.deliveries,source('deliveries'),{
    transform:record=>({
      ...record,
      registrationId:registrationResult.idMap.get(record.registrationId) || record.registrationId,
      sessionId:sessionResult.idMap.get(record.sessionId) || record.sessionId
    })
  });
  const workflowResult=mergeCollection(current.workflows,source('workflows'));
  const logResult=mergeCollection(current.logs,source('logs'));

  const merged={
    contacts:contactResult.rows,
    webinars:webinarResult.rows,
    sessions:sessionResult.rows,
    registrations:registrationResult.rows,
    deliveries:deliveryResult.rows,
    workflows:workflowResult.rows,
    logs:logResult.rows
  };
  Object.entries(merged).forEach(([name,records])=>save(DATA_KEYS[name],records));
  return Object.fromEntries(names.map(name=>[name,merged[name].length-current[name].length]));
}
